import {createStyles, Group, Paper, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {EventTriggerConfig, Routine, RoutineStatus, TimeTriggerConfig, TriggerType} from './RoutineTypes';
import {getLowerCasedObjectTypeLabel} from './routineUtils';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIME_LABELS: Record<string, string> = {
  '08:00': 'AM',
  '12:00': 'Midday',
  '18:00': 'PM',
};

function formatTimeTrigger(config: TimeTriggerConfig): string {
  const interval = config.recurrence_interval ?? 1;
  const freqLabel =
    config.freq_type === 'daily'
      ? interval === 1
        ? 'day'
        : `${interval} days`
      : config.freq_type === 'weekly'
        ? interval === 1
          ? 'week'
          : `${interval} weeks`
        : interval === 1
          ? 'month'
          : `${interval} months`;

  const dayPart =
    config.weekly_days != null && config.weekly_days.length > 0
      ? `, ${config.weekly_days.map((d) => DAY_NAMES[d]).join(', ')}`
      : '';
  const timePart = TIME_LABELS[config.run_time] ?? config.run_time;
  return `Send every ${freqLabel}${dayPart} ${timePart}`;
}

function formatEventTrigger(config: EventTriggerConfig): string {
  const objectIds = config.object_ids ?? [];
  const objectTypeLabel = getLowerCasedObjectTypeLabel(config.object_type);

  if (config.trigger_kind === 'object_created') {
    return config.schema_id == null || config.schema_id === ''
      ? `Send when ${objectTypeLabel} is created`
      : `Send when ${objectTypeLabel} is created for schema`;
  }

  if (config.schema_id != null && config.schema_id !== '') {
    return `Send on ${objectTypeLabel} field change for schema`;
  }

  if (objectIds.length === 0) {
    return `Send on ${objectTypeLabel} field change`;
  }
  const objectIdLabel =
    objectIds.length === 1 ? objectIds[0].replace(/^[a-z]+_/, '') : `${objectIds.length} objects`;
  return `Send on ${objectTypeLabel} field change, for ${objectIdLabel}`;
}

function formatNextRun(nextRunAt: string | null, status: RoutineStatus): string {
  if (status === RoutineStatus.PAUSED) {
    return 'Paused';
  }
  if (status === RoutineStatus.PENDING_REGISTRATION) {
    return 'Registering...';
  }
  if (nextRunAt == null) {
    // TODO(nweeden): nextRunAt is not yet returned by the backend; always null for now.
    return 'Waiting';
  }
  const now = new Date();
  const next = new Date(nextRunAt);
  const diffMs = next.getTime() - now.getTime();
  const diffDays = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  return `in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
}

interface RoutineCardProps {
  routine: Routine;
  onClick?: (routine: Routine) => void;
}

const useStyles = createStyles((theme) => ({
  clickable: {
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.colors.gray[5],
    },
  },
  prompt: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  triggerIcon: {
    fontSize: 12,
    color: theme.colors.gray[6],
    flexShrink: 0,
  },
}));

export default function RoutineCard({routine, onClick}: RoutineCardProps): JSX.Element {
  const {classes, cx} = useStyles();
  const isTimeBased = routine.triggerType === TriggerType.TIME_BASED;
  const triggerText = isTimeBased
    ? formatTimeTrigger(routine.triggerConfig as TimeTriggerConfig)
    : formatEventTrigger(routine.triggerConfig as EventTriggerConfig);
  const displayName = routine.name ?? 'Untitled background agent';
  const statusText = formatNextRun(routine.nextRunAt, routine.status);

  const card = (
    <Paper withBorder={true} p="sm" className={cx(onClick != null && classes.clickable)}>
      <Group noWrap={true} align="flex-start" position="apart">
        <Stack spacing={4} style={{minWidth: 0, flex: 1}}>
          <Text size="sm" weight={600} truncate={true}>
            {displayName}
          </Text>
          <Text size="xs" color="dimmed" className={classes.prompt}>
            {routine.prompt}
          </Text>
          <Group spacing={4} noWrap={true}>
            <BenchlingIcon icon={isTimeBased ? 'clock' : 'sparkle'} className={classes.triggerIcon} />
            <Text size="xs" color="dimmed" style={{whiteSpace: 'nowrap'}}>
              {triggerText}
            </Text>
          </Group>
        </Stack>
        <Text size="sm" style={{whiteSpace: 'nowrap', flexShrink: 0}}>
          {statusText}
        </Text>
      </Group>
    </Paper>
  );

  if (onClick == null) {
    return card;
  }

  return (
    <UnstyledButton
      aria-label={displayName}
      sx={{display: 'block', width: '100%'}}
      onClick={() => {
        onClick(routine);
      }}
    >
      {card}
    </UnstyledButton>
  );
}
